import { createFileRoute, Link } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { useTenant } from '../lib/tenant-context';
import { tenantFetch } from '../lib/api-client';
import { RequirePlan } from '../components/RouteGuard';
import { Bell } from "@solar-icons/react";

// Types
interface Campaign {
  id: string;
  name: string;
  description: string | null;
  status: 'draft' | 'active' | 'paused' | 'completed' | 'cancelled';
  target_brands: string[];
  target_categories: string[];
  offer_type: 'percentage' | 'fixed' | 'tiered';
  offer_percentage: number | null;
  offer_fixed_cents: number | null;
  bonus_percentage: number;
  budget_cents: number | null;
  spent_cents: number;
  reserved_cents: number;
  offers_sent: number;
  max_offers: number | null;
  starts_at: number | null;
  ends_at: number | null;
  created_at: number;
  total_offers: number;
  accepted_offers: number;
}

interface CampaignFormData {
  name: string;
  description: string;
  target_brands: string[];
  target_categories: string[];
  offer_type: 'percentage' | 'fixed';
  offer_percentage: number;
  offer_fixed_cents: number;
  bonus_percentage: number;
  budget_cents: number;
  max_offers: number;
}

// Icons
const PlusIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M10 4v12M4 10h12" strokeLinecap="round" />
  </svg>
);

const CampaignIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M3 10h2l3-6 4 12 3-6h2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const PlayIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
    <path d="M4 3.5v9l8-4.5-8-4.5z" />
  </svg>
);

const PauseIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
    <rect x="4" y="3" width="3" height="10" rx="1" />
    <rect x="9" y="3" width="3" height="10" rx="1" />
  </svg>
);

const XIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M5 5l10 10M15 5l-10 10" strokeLinecap="round" />
  </svg>
);

const RefreshIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M2 8a6 6 0 0111.472-2.5M14 8a6 6 0 01-11.472 2.5" strokeLinecap="round" />
    <path d="M13 3v3h-3M3 13v-3h3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Demo data generator
function getDemoCampaigns(tenantId: string): Campaign[] {
  const now = Math.floor(Date.now() / 1000);
  
  if (tenantId === 'lululemon') {
    return [
      {
        id: 'cmp_lulu_1',
        name: 'Lululemon Winter Trade-In',
        description: 'Get credit for your gently used Lululemon items',
        status: 'active',
        target_brands: ['Lululemon'],
        target_categories: ['Clothing', 'Activewear'],
        offer_type: 'percentage',
        offer_percentage: 0.35,
        offer_fixed_cents: null,
        bonus_percentage: 0.15,
        budget_cents: 2500000,
        spent_cents: 875000,
        reserved_cents: 125000,
        offers_sent: 47,
        max_offers: 200,
        starts_at: now - 86400 * 14,
        ends_at: now + 86400 * 30,
        created_at: now - 86400 * 14,
        total_offers: 47,
        accepted_offers: 23
      },
      {
        id: 'cmp_lulu_2',
        name: 'Holiday Leggings Takeback',
        description: 'Special holiday offer for Align and Wunder Under leggings',
        status: 'draft',
        target_brands: ['Lululemon'],
        target_categories: ['Leggings'],
        offer_type: 'percentage',
        offer_percentage: 0.40,
        offer_fixed_cents: null,
        bonus_percentage: 0.10,
        budget_cents: 1000000,
        spent_cents: 0,
        reserved_cents: 0,
        offers_sent: 0,
        max_offers: 100,
        starts_at: null,
        ends_at: null,
        created_at: now - 86400 * 2,
        total_offers: 0,
        accepted_offers: 0
      }
    ];
  }
  
  if (tenantId === 'nespresso') {
    return [
      {
        id: 'cmp_nes_1',
        name: 'Pod Recycling Rewards',
        description: 'Earn credits for returning used Nespresso pods',
        status: 'active',
        target_brands: ['Nespresso'],
        target_categories: ['Coffee Pods'],
        offer_type: 'fixed',
        offer_percentage: null,
        offer_fixed_cents: 500,
        bonus_percentage: 0.20,
        budget_cents: 5000000,
        spent_cents: 1250000,
        reserved_cents: 75000,
        offers_sent: 2500,
        max_offers: null,
        starts_at: now - 86400 * 60,
        ends_at: null,
        created_at: now - 86400 * 60,
        total_offers: 2500,
        accepted_offers: 1875
      }
    ];
  }
  
  if (tenantId === 'lego') {
    return [
      {
        id: 'cmp_lego_1',
        name: 'LEGO Brick Buyback Program',
        description: 'Trade in your used LEGO bricks for store credit',
        status: 'active',
        target_brands: ['LEGO'],
        target_categories: ['Building Sets', 'Loose Bricks'],
        offer_type: 'percentage',
        offer_percentage: 0.30,
        offer_fixed_cents: null,
        bonus_percentage: 0.15,
        budget_cents: 10000000,
        spent_cents: 3420000,
        reserved_cents: 580000,
        offers_sent: 456,
        max_offers: 2000,
        starts_at: now - 86400 * 45,
        ends_at: now + 86400 * 120,
        created_at: now - 86400 * 45,
        total_offers: 456,
        accepted_offers: 342
      },
      {
        id: 'cmp_lego_2',
        name: 'Star Wars Set Collection',
        description: 'Premium offers for complete Star Wars LEGO sets',
        status: 'active',
        target_brands: ['LEGO Star Wars'],
        target_categories: ['Star Wars', 'UCS Sets'],
        offer_type: 'percentage',
        offer_percentage: 0.45,
        offer_fixed_cents: null,
        bonus_percentage: 0.10,
        budget_cents: 5000000,
        spent_cents: 1250000,
        reserved_cents: 350000,
        offers_sent: 89,
        max_offers: 300,
        starts_at: now - 86400 * 14,
        ends_at: now + 86400 * 90,
        created_at: now - 86400 * 14,
        total_offers: 89,
        accepted_offers: 56
      },
      {
        id: 'cmp_lego_3',
        name: 'LEGO Replay Donation',
        description: 'Donate bricks to children in need - earn gift cards',
        status: 'active',
        target_brands: ['LEGO'],
        target_categories: ['Any'],
        offer_type: 'fixed',
        offer_percentage: null,
        offer_fixed_cents: 1000,
        bonus_percentage: 0.25,
        budget_cents: 3000000,
        spent_cents: 890000,
        reserved_cents: 110000,
        offers_sent: 890,
        max_offers: null,
        starts_at: now - 86400 * 90,
        ends_at: null,
        created_at: now - 86400 * 90,
        total_offers: 890,
        accepted_offers: 712
      }
    ];
  }

  if (tenantId === 'nike') {
    return [
      {
        id: 'cmp_nike_1',
        name: 'Nike Refurbished Trade-In',
        description: 'Trade in your gently used Nike footwear for store credit',
        status: 'active',
        target_brands: ['Nike', 'Jordan'],
        target_categories: ['Footwear', 'Sneakers'],
        offer_type: 'percentage',
        offer_percentage: 0.45,
        offer_fixed_cents: null,
        bonus_percentage: 0.05,
        budget_cents: 10000000,
        spent_cents: 3450000,
        reserved_cents: 550000,
        offers_sent: 312,
        max_offers: 1000,
        starts_at: now - 86400 * 30,
        ends_at: now + 86400 * 90,
        created_at: now - 86400 * 30,
        total_offers: 312,
        accepted_offers: 187
      },
      {
        id: 'cmp_nike_2',
        name: 'Jordan Collection Buyback',
        description: 'Premium offers for Air Jordan retro sneakers',
        status: 'active',
        target_brands: ['Jordan'],
        target_categories: ['Jordan', 'Retro'],
        offer_type: 'percentage',
        offer_percentage: 0.50,
        offer_fixed_cents: null,
        bonus_percentage: 0.10,
        budget_cents: 5000000,
        spent_cents: 1875000,
        reserved_cents: 375000,
        offers_sent: 89,
        max_offers: 250,
        starts_at: now - 86400 * 14,
        ends_at: now + 86400 * 60,
        created_at: now - 86400 * 14,
        total_offers: 89,
        accepted_offers: 62
      },
      {
        id: 'cmp_nike_3',
        name: 'Nike Running Trade-Up',
        description: 'Trade in old running shoes for credit toward new performance footwear',
        status: 'active',
        target_brands: ['Nike'],
        target_categories: ['Running', 'Performance'],
        offer_type: 'percentage',
        offer_percentage: 0.35,
        offer_fixed_cents: null,
        bonus_percentage: 0.15,
        budget_cents: 3000000,
        spent_cents: 890000,
        reserved_cents: 110000,
        offers_sent: 156,
        max_offers: 500,
        starts_at: now - 86400 * 21,
        ends_at: now + 86400 * 45,
        created_at: now - 86400 * 21,
        total_offers: 156,
        accepted_offers: 98
      }
    ];
  }

  // Arc'teryx default
  return [
    {
      id: 'cmp_arc_1',
      name: 'Arc\'teryx ReBird Trade-In',
      description: 'Trade in your used Arc\'teryx gear for store credit',
      status: 'active',
      target_brands: ['Arc\'teryx'],
      target_categories: ['Outerwear', 'Clothing'],
      offer_type: 'percentage',
      offer_percentage: 0.40,
      offer_fixed_cents: null,
      bonus_percentage: 0.10,
      budget_cents: 5000000,
      spent_cents: 1875000,
      reserved_cents: 250000,
      offers_sent: 156,
      max_offers: 500,
      starts_at: now - 86400 * 30,
      ends_at: now + 86400 * 60,
      created_at: now - 86400 * 30,
      total_offers: 156,
      accepted_offers: 89
    },
    {
      id: 'cmp_arc_2',
      name: 'Shell Jacket Program',
      description: 'Premium offers for Alpha and Beta shell jackets',
      status: 'active',
      target_brands: ['Arc\'teryx'],
      target_categories: ['Shell Jackets'],
      offer_type: 'percentage',
      offer_percentage: 0.45,
      offer_fixed_cents: null,
      bonus_percentage: 0.05,
      budget_cents: 3000000,
      spent_cents: 450000,
      reserved_cents: 75000,
      offers_sent: 28,
      max_offers: 150,
      starts_at: now - 86400 * 7,
      ends_at: now + 86400 * 90,
      created_at: now - 86400 * 7,
      total_offers: 28,
      accepted_offers: 12
    },
    {
      id: 'cmp_arc_3',
      name: 'Backpack Buyback',
      description: 'Trade in old backpacks for credit towards new gear',
      status: 'paused',
      target_brands: ['Arc\'teryx'],
      target_categories: ['Backpacks', 'Bags'],
      offer_type: 'fixed',
      offer_percentage: null,
      offer_fixed_cents: 7500,
      bonus_percentage: 0,
      budget_cents: 1000000,
      spent_cents: 225000,
      reserved_cents: 0,
      offers_sent: 30,
      max_offers: 100,
      starts_at: now - 86400 * 45,
      ends_at: now + 86400 * 15,
      created_at: now - 86400 * 45,
      total_offers: 30,
      accepted_offers: 18
    }
  ];
}

// Status badge component - Pentatonic colors
function StatusBadge({ status }: { status: Campaign['status'] }) {
  const config = {
    active: { bg: '#00FBA915', color: '#00C48C', label: 'Active' },
    draft: { bg: '#F5F5F5', color: '#737373', label: 'Draft' },
    paused: { bg: '#FFF8E1', color: '#A3A3A3', label: 'Paused' },
    completed: { bg: '#E5E5E5', color: '#525252', label: 'Completed' },
    cancelled: { bg: '#FEE2E2', color: '#A3A3A3', label: 'Cancelled' }
  };
  
  const { bg, color, label } = config[status];
  
  return (
    <span 
      className="px-2 py-1 rounded text-xs font-light"
      style={{ backgroundColor: bg, color }}
    >
      {label}
    </span>
  );
}

// Format currency
function formatCurrency(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(cents / 100);
}

// Format percentage
function formatPercent(value: number): string {
  return `${Math.round(value * 100)}%`;
}

// Create Campaign Modal
function CreateCampaignModal({ 
  isOpen, 
  onClose, 
  onSubmit 
}: { 
  isOpen: boolean; 
  onClose: () => void; 
  onSubmit: (data: CampaignFormData) => void;
}) {
  const [formData, setFormData] = useState<CampaignFormData>({
    name: '',
    description: '',
    target_brands: [],
    target_categories: [],
    offer_type: 'percentage',
    offer_percentage: 40,
    offer_fixed_cents: 5000,
    bonus_percentage: 10,
    budget_cents: 100000,
    max_offers: 100
  });

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div 
        className="relative bg-white shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto"
        style={{ borderColor: 'rgba(169,169,169,0.2)' }}
      >
        <div className="flex items-center justify-between p-6 border-b" style={{ borderColor: 'rgba(169,169,169,0.1)' }}>
          <h2 className="text-lg font-light" style={{ color: '#171717' }}>Create Campaign</h2>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100 transition-colors">
            <XIcon />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: '#171717' }}>Campaign Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border text-sm"
              style={{ borderColor: 'rgba(169,169,169,0.3)' }}
              placeholder="e.g., Holiday Trade-In Special"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: '#171717' }}>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border text-sm"
              style={{ borderColor: 'rgba(169,169,169,0.3)' }}
              rows={2}
              placeholder="Describe your campaign..."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: '#171717' }}>Offer Type</label>
              <select
                value={formData.offer_type}
                onChange={(e) => setFormData({ ...formData, offer_type: e.target.value as 'percentage' | 'fixed' })}
                className="w-full px-3 py-2 border text-sm"
                style={{ borderColor: 'rgba(169,169,169,0.3)' }}
              >
                <option value="percentage">Percentage of Value</option>
                <option value="fixed">Fixed Amount</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: '#171717' }}>
                {formData.offer_type === 'percentage' ? 'Offer %' : 'Fixed Amount'}
              </label>
              <input
                type="number"
                value={formData.offer_type === 'percentage' ? formData.offer_percentage : formData.offer_fixed_cents / 100}
                onChange={(e) => {
                  const val = parseFloat(e.target.value);
                  if (formData.offer_type === 'percentage') {
                    setFormData({ ...formData, offer_percentage: val });
                  } else {
                    setFormData({ ...formData, offer_fixed_cents: val * 100 });
                  }
                }}
                className="w-full px-3 py-2 border text-sm"
                style={{ borderColor: 'rgba(169,169,169,0.3)' }}
                min="0"
                step={formData.offer_type === 'percentage' ? '5' : '1'}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: '#171717' }}>Bonus %</label>
              <input
                type="number"
                value={formData.bonus_percentage}
                onChange={(e) => setFormData({ ...formData, bonus_percentage: parseFloat(e.target.value) })}
                className="w-full px-3 py-2 border text-sm"
                style={{ borderColor: 'rgba(169,169,169,0.3)' }}
                min="0"
                max="50"
                step="5"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: '#171717' }}>Budget</label>
              <input
                type="number"
                value={formData.budget_cents / 100}
                onChange={(e) => setFormData({ ...formData, budget_cents: parseFloat(e.target.value) * 100 })}
                className="w-full px-3 py-2 border text-sm"
                style={{ borderColor: 'rgba(169,169,169,0.3)' }}
                min="0"
                step="100"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: '#171717' }}>Max Offers</label>
            <input
              type="number"
              value={formData.max_offers}
              onChange={(e) => setFormData({ ...formData, max_offers: parseInt(e.target.value) })}
              className="w-full px-3 py-2 border text-sm"
              style={{ borderColor: 'rgba(169,169,169,0.3)' }}
              min="1"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border text-sm font-medium hover:bg-gray-50 transition-colors"
              style={{ borderColor: 'rgba(169,169,169,0.3)', color: '#171717' }}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 rounded-full text-sm font-medium text-white transition-colors"
              style={{ backgroundColor: '#171717' }}
            >
              Create Campaign
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Main component
function CampaignsPage() {
  const { currentTenant } = useTenant();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [runningCampaign, setRunningCampaign] = useState<string | null>(null);

  useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        const response = await tenantFetch('/api/v1/campaigns');
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data?.campaigns?.length > 0) {
            setCampaigns(data.data.campaigns);
          } else {
            setCampaigns(getDemoCampaigns(currentTenant.id));
          }
        } else {
          setCampaigns(getDemoCampaigns(currentTenant.id));
        }
      } catch {
        setCampaigns(getDemoCampaigns(currentTenant.id));
      } finally {
        setLoading(false);
      }
    };
    
    fetchCampaigns();
  }, [currentTenant.id]);

  const handleCreateCampaign = async (data: CampaignFormData) => {
    try {
      const response = await tenantFetch('/api/v1/campaigns', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...data,
          offer_percentage: data.offer_type === 'percentage' ? data.offer_percentage / 100 : null,
          offer_fixed_cents: data.offer_type === 'fixed' ? data.offer_fixed_cents : null,
          bonus_percentage: data.bonus_percentage / 100
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setCampaigns(prev => [result.data, ...prev]);
        }
      }
    } catch (error) {
      console.error('Failed to create campaign:', error);
    }
  };

  const handleStatusChange = async (campaignId: string, newStatus: 'active' | 'paused') => {
    try {
      const response = await tenantFetch(`/api/v1/campaigns/${campaignId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      
      if (response.ok) {
        setCampaigns(prev => prev.map(c => 
          c.id === campaignId ? { ...c, status: newStatus } : c
        ));
      }
    } catch (error) {
      console.error('Failed to update campaign:', error);
    }
  };

  const handleRunCampaign = async (campaignId: string) => {
    setRunningCampaign(campaignId);
    try {
      await tenantFetch(`/api/v1/campaigns/${campaignId}/run`, {
        method: 'POST'
      });
      // Refresh campaigns
      const response = await tenantFetch('/api/v1/campaigns');
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setCampaigns(data.data.campaigns);
        }
      }
    } catch (error) {
      console.error('Failed to run campaign:', error);
    } finally {
      setRunningCampaign(null);
    }
  };

  // Calculate totals
  const totalBudget = campaigns.reduce((sum, c) => sum + (c.budget_cents || 0), 0);
  const totalOffers = campaigns.reduce((sum, c) => sum + c.total_offers, 0);
  const totalAccepted = campaigns.reduce((sum, c) => sum + c.accepted_offers, 0);
  const activeCampaigns = campaigns.filter(c => c.status === 'active').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div 
          className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin"
          style={{ borderColor: currentTenant.color, borderTopColor: 'transparent' }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-light" style={{ color: '#171717' }}>
            Takeback Campaigns
          </h1>
          <p className="text-sm mt-1" style={{ color: '#737373' }}>
            Automatically match brand offers to consumer Thingventories
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/campaign-analytics"
            className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-light transition-colors hover:bg-gray-50"
            style={{ border: '1px solid #E5E5E5', color: '#525252' }}
          >
            <CampaignIcon />
            Analytics
          </Link>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-full text-white text-sm font-light transition-opacity hover:opacity-90"
            style={{ backgroundColor: currentTenant.color }}
          >
            <PlusIcon />
            New Campaign
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="p-5" style={{ backgroundColor: '#FAFAFA', border: '1px solid #E5E5E5' }}>
          <p className="text-sm font-light mb-2" style={{ color: '#737373' }}>Active Campaigns</p>
          <p className="text-2xl font-light" style={{ color: '#171717' }}>{activeCampaigns}</p>
        </div>
        <div className="p-5" style={{ backgroundColor: '#FAFAFA', border: '1px solid #E5E5E5' }}>
          <p className="text-sm font-light mb-2" style={{ color: '#737373' }}>Total Budget</p>
          <p className="text-2xl font-light" style={{ color: '#171717' }}>{formatCurrency(totalBudget)}</p>
        </div>
        <div className="p-5" style={{ backgroundColor: '#FAFAFA', border: '1px solid #E5E5E5' }}>
          <p className="text-sm font-light mb-2" style={{ color: '#737373' }}>Offers Sent</p>
          <p className="text-2xl font-light" style={{ color: '#171717' }}>{totalOffers.toLocaleString()}</p>
        </div>
        <div className="p-5" style={{ backgroundColor: '#FAFAFA', border: '1px solid #E5E5E5' }}>
          <p className="text-sm font-light mb-2" style={{ color: '#737373' }}>Conversion Rate</p>
          <p className="text-2xl font-light" style={{ color: currentTenant.color }}>
            {totalOffers > 0 ? `${((totalAccepted / totalOffers) * 100).toFixed(1)}%` : '0%'}
          </p>
        </div>
      </div>

      {/* Campaigns List */}
      <div style={{ backgroundColor: '#FAFAFA', border: '1px solid #E5E5E5' }}>
        <div className="p-4 flex items-center justify-between" style={{ borderBottom: '1px solid #E5E5E5' }}>
          <h2 className="font-light" style={{ color: '#171717' }}>All Campaigns</h2>
        </div>

        {campaigns.length === 0 ? (
          <div className="p-12 text-center">
            <CampaignIcon />
            <h3 className="mt-4 font-light" style={{ color: '#171717' }}>No campaigns yet</h3>
            <p className="text-sm mt-1 font-light" style={{ color: '#737373' }}>
              Create your first campaign to start matching offers to consumer Thingventories
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="mt-4 px-4 py-2 rounded-full text-white text-sm font-light transition-opacity hover:opacity-90"
              style={{ backgroundColor: currentTenant.color }}
            >
              Create Campaign
            </button>
          </div>
        ) : (
          <div>
            {campaigns.map(campaign => (
              <div key={campaign.id} className="p-4 hover:bg-white transition-colors" style={{ borderBottom: '1px solid #E5E5E5' }}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <Link 
                        to="/campaigns/$id"
                        params={{ id: campaign.id }}
                        className="font-light hover:underline"
                        style={{ color: '#171717' }}
                      >
                        {campaign.name}
                      </Link>
                      <StatusBadge status={campaign.status} />
                    </div>
                    
                    <p className="text-sm mb-3 font-light" style={{ color: '#737373' }}>
                      {campaign.description || 'No description'}
                    </p>

                    <div className="flex items-center gap-6 text-sm">
                      <div>
                        <span style={{ color: '#737373' }}>Offer: </span>
                        <span style={{ color: '#171717' }}>
                          {campaign.offer_type === 'percentage' && campaign.offer_percentage
                            ? `${formatPercent(campaign.offer_percentage)} + ${formatPercent(campaign.bonus_percentage)} bonus`
                            : campaign.offer_fixed_cents
                              ? `${formatCurrency(campaign.offer_fixed_cents)} + ${formatPercent(campaign.bonus_percentage)} bonus`
                              : 'N/A'}
                        </span>
                      </div>
                      <div>
                        <span style={{ color: '#737373' }}>Budget: </span>
                        <span style={{ color: '#171717' }}>
                          {campaign.budget_cents 
                            ? `${formatCurrency(campaign.spent_cents)} / ${formatCurrency(campaign.budget_cents)}`
                            : 'Unlimited'}
                        </span>
                      </div>
                      <div>
                        <span style={{ color: '#737373' }}>Offers: </span>
                        <span style={{ color: '#171717' }}>
                          {campaign.total_offers} sent, {campaign.accepted_offers} accepted
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {campaign.status === 'active' && (
                      <>
                        <button
                          onClick={() => handleRunCampaign(campaign.id)}
                          disabled={runningCampaign === campaign.id}
                          className="flex items-center gap-1 px-3 py-1.5 rounded-full text-sm border transition-colors hover:bg-gray-50 disabled:opacity-50"
                          style={{ borderColor: 'rgba(169,169,169,0.3)', color: '#171717' }}
                        >
                          <RefreshIcon />
                          {runningCampaign === campaign.id ? 'Running...' : 'Run Now'}
                        </button>
                        <button
                          onClick={() => handleStatusChange(campaign.id, 'paused')}
                          className="flex items-center gap-1 px-3 py-1.5 rounded-full text-sm border transition-colors hover:bg-gray-50"
                          style={{ borderColor: 'rgba(169,169,169,0.3)', color: '#E65100' }}
                        >
                          <PauseIcon />
                          Pause
                        </button>
                      </>
                    )}
                    {(campaign.status === 'paused' || campaign.status === 'draft') && (
                      <button
                        onClick={() => handleStatusChange(campaign.id, 'active')}
                        className="flex items-center gap-1 px-3 py-1.5 rounded-full text-sm text-white transition-colors hover:opacity-90"
                        style={{ backgroundColor: '#2E7D32' }}
                      >
                        <PlayIcon />
                        {campaign.status === 'draft' ? 'Launch' : 'Resume'}
                      </button>
                    )}
                  </div>
                </div>

                {/* Budget progress bar */}
                {campaign.budget_cents && (
                  <div className="mt-3">
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full transition-all duration-300"
                        style={{ 
                          width: `${Math.min(100, (campaign.spent_cents / campaign.budget_cents) * 100)}%`,
                          backgroundColor: campaign.spent_cents >= campaign.budget_cents * 0.9 ? '#E65100' : '#171717'
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Modal */}
      <CreateCampaignModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreateCampaign}
      />
    </div>
  );
}

export const Route = createFileRoute('/campaigns')({
  component: () => (
    <RequirePlan required="starter" featureName="Campaigns" icon={<Bell size={64} weight="LineDuotone" />}>
      <CampaignsPage />
    </RequirePlan>
  ),
});

